// hooks/useListProcessor.js
import { useState, useCallback, useEffect } from "react";
import { processLists } from "../../utils/listProcessor";

export const useListProcessor = (options = {}) => {
  const { showAlerts = true, autoSave = false, onSuccess, onError } = options;

  // State for hook
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({
    totalProcessed: 0,
    totalListsCreated: 0,
    totalListsUpdated: 0,
  });

  const normalizeDataGroupForLists = (sdataGroup) => {
    if (!sdataGroup || typeof sdataGroup !== "object") return [];

    return Object.entries(sdataGroup)
      .map(([key, data]) => {
        if (!data || typeof data !== "object") return null;

        const questionNumber =
          data.Question_number || data.question_number || key;

        // Detect structure
        const isKeyBasedList =
          Array.isArray(data.values) ||
          Array.isArray(data.Rows) ||
          Array.isArray(data.Cols) ||
          Array.isArray(data.Stats);

        return {
          questionNumber,
          type: data.type || data.Type || "",
          // Normalize list containers
          values: isKeyBasedList ? data.values || [] : [],
          rows: isKeyBasedList ? data.Rows || [] : data.rows || [],
          cols: isKeyBasedList ? data.Cols || [] : data.columns || [],
          stats: isKeyBasedList ? data.Stats || [] : [],
          original: data,
        };
      })
      .filter(Boolean);
  };

  // Main processing function
  const addAllLists = useCallback(
    async (sdataGroup, existingLists, setList) => {
      setIsProcessing(true);
      setError(null);

      try {
        // Validate inputs
        if (!setList || typeof setList !== "function") {
          throw new Error("setList must be a function");
        }

        // Process lists using utility function
        const normalizedData = normalizeDataGroupForLists(sdataGroup);
        const result = processLists(normalizedData, existingLists);

        // Update list state
        setList(result.allLists);

        // Update hook state
        setLastResult(result);
        setHistory((prev) => [
          ...prev.slice(-9), // Keep last 10 items
          {
            timestamp: new Date().toISOString(),
            summary: result.summary,
            totalLists: result.allLists.length,
          },
        ]);

        // Update stats
        setStats((prev) => ({
          totalProcessed: prev.totalProcessed + 1,
          totalListsCreated: prev.totalListsCreated + result.listsCreated,
          totalListsUpdated: prev.totalListsUpdated + result.listsUpdated,
        }));

        // Auto-save if enabled
        if (autoSave && typeof autoSave === "function") {
          try {
            await autoSave(result.allLists);
          } catch (saveError) {
            console.warn("Auto-save failed:", saveError);
          }
        }

        // Show alert if enabled
        if (showAlerts) {
          const alertMessage =
            `✅ List processing completed!\n\n📊 Summary:\n` +
            `• Total lists: ${result.summary.total}\n` +
            `• Multi-question lists: ${result.summary.multiQuestion}\n` +
            `• Single-question lists: ${result.summary.singleQuestion}\n` +
            `• New multi-question lists created: ${result.summary.created}\n` +
            `• Existing lists updated: ${result.summary.updated}\n\n` +
            `Note: Manually saved lists are preserved.`;

          alert(alertMessage);
        }

        // Call success callback
        if (onSuccess) {
          onSuccess(result);
        }

        setIsProcessing(false);
        return result;
      } catch (err) {
        const errorMessage = err.message || "An unknown error occurred";
        setError(errorMessage);

        // Show error alert if enabled
        if (showAlerts) {
          alert(`❌ Error: ${errorMessage}`);
        }

        // Call error callback
        if (onError) {
          onError(err);
        }

        setIsProcessing(false);
        throw err;
      }
    },
    [showAlerts, autoSave, onSuccess, onError]
  );

  // Helper function to get processing summary
  const getSummary = useCallback(() => {
    if (!lastResult) return null;

    return {
      message: `Processed ${lastResult.summary.total} lists`,
      details: lastResult.summary,
      timestamp: new Date().toLocaleString(),
    };
  }, [lastResult]);

  // Helper to clear history
  const clearHistory = useCallback(() => {
    setHistory([]);
  }, []);

  // Helper to undo last operation (if needed)
  const undoLast = useCallback(
    (setList) => {
      if (history.length > 1 && lastResult) {
        // This would need your specific undo logic
        // For now, just a placeholder
        // console.log ('Undo functionality would go here');
      }
    },
    [history, lastResult]
  );

  // Reset hook state
  const reset = useCallback(() => {
    setIsProcessing(false);
    setLastResult(null);
    setError(null);
  }, []);

  return {
    // Main function
    addAllLists,

    // State
    isProcessing,
    lastResult,
    error,
    history,
    stats,

    // Helpers
    getSummary,
    clearHistory,
    undoLast,
    reset,

    // Convenience properties
    hasResults: !!lastResult,
    canUndo: history.length > 1,
    lastProcessedCount: lastResult?.summary?.total || 0,
  };
};

// Hook for analytics tracking
export const useListProcessorWithAnalytics = (analyticsService) => {
  const baseProcessor = useListProcessor({
    onSuccess: (result) => {
      if (analyticsService) {
        analyticsService.track("lists_processed", {
          total: result.summary.total,
          created: result.summary.created,
          updated: result.summary.updated,
        });
      }
    },
    onError: (error) => {
      if (analyticsService) {
        analyticsService.track("list_processing_error", {
          error: error.message,
        });
      }
    },
  });

  return baseProcessor;
};
